//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  DetectLanguageResponse,
  HistoricalUrlsResponse,
  IGetTermsAndConditions,
  IRightsCapabilitiesMultiHandleResponse,
  ITermsAndConditions,
  ITermsAndConditionsResponse,
  InitiateRightsRequestRequest,
  InitiateRightsRequestResponse,
  MarkRightsApprovedRequest,
  MarkRightsApprovedResponse,
  RightsConfigurationsRequest,
  RightsConfigurationsResponse,
  SetRightsExpiryDateRequest,
  SkipRightsRequest,
  SyndicatedContentStatusRequest,
  SyndicatedContentStatusResponse,
} from '@apis/rights-management/rightsManagementApiModels';
import { deserializeRightsPermissions } from '@apis/rights-management/rightsManagementApiUtils';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { makeParams } from '@utils/makeParams';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const UIA_BASE_URL = '/api/universal-image-actions';
const RIGHTS_API_BASE_URL = '/api/v1/rights/terms-and-conditions';
const RIGHTS_MANAGEMENT_BASE_URL = '/api/rights-management';

const rightsManagementFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedSessionFetch(
    `${RIGHTS_MANAGEMENT_BASE_URL}/${endpoint}`,
    init,
  );
};

const webGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch('web_gateway', endpoint, init);
};

const gatewayRightsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/rights/${endpoint}`,
    init,
  );
};

const syndicationFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'syndication_gateway',
    `/api/v1/connect/${endpoint}`,
    init,
  );
};

const uiaFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedSessionFetch(`${UIA_BASE_URL}/${endpoint}`, init);
};

/**
 * Gets the TermsAndConditions url associated with the given client
 * /api/v1/rights/terms-and-conditions/url
 *
 * @returns   TermsAndConditions url as a promise
 */
export async function getTermsAndConditions(
  init?: RequestInit,
): Promise<IGetTermsAndConditions> {
  const res = await webGatewayFetch(`${RIGHTS_API_BASE_URL}/url`, init);
  const response = res.json<IGetTermsAndConditions>();
  return response;
}

/**
 * Put the TermsAndConditions url associated with the given client
 * /api/v1/rights/terms-and-conditions/url
 *
 * @returns TermsAndConditions as a promise
 */
export async function updateTermsAndConditions(
  request: ITermsAndConditions,
  init?: RequestInit,
): Promise<ITermsAndConditions> {
  const res = await webGatewayFetch(`${RIGHTS_API_BASE_URL}/url`, {
    ...init,
    method: 'PUT',
    body: JSON.stringify(request),
  });
  const response = await res.json<ITermsAndConditionsResponse>();
  return response.data;
}

/**
 * Marks the given media items as being approved for distribution,
 * with an optional expirationTime
 *
 * POST /api/v1/rights/record-rights
 */
export async function markRightsApproved(
  request: MarkRightsApprovedRequest,
  init?: RequestInit,
): Promise<MarkRightsApprovedResponse> {
  const res = await gatewayRightsFetch('record-rights', {
    ...init,
    method: 'POST',
    body: JSON.stringify(request),
  });

  return await res.json<{ data: MarkRightsApprovedResponse }>((res) => {
    const data: MarkRightsApprovedResponse = {};
    Object.entries(res.data).forEach(([cuid, permission]) => {
      data[PartialCuid(cuid)] = deserializeRightsPermissions(permission);
    });

    return {
      data,
    };
  });
}

/**
 * Skips the rights request for the given media items.
 *
 * POST /api/v1/rights/skip-rights
 */
export async function skipRights(
  request: SkipRightsRequest,
  init?: RequestInit,
): Promise<void> {
  await gatewayRightsFetch('skip-rights', {
    ...init,
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Initiate item rights request
 *
 * PUT /api/universal-image-actions/items/:cuid/initiate-rights-request
 */
export const initiateRightsRequest = async (
  cuid: Cuid,
  request: InitiateRightsRequestRequest,
  init?: RequestInit,
): Promise<InitiateRightsRequestResponse> => {
  const res = await gatewayRightsFetch(
    `items/${cuid}/initiate-rights-request`,
    {
      ...init,
      method: 'PUT',
      body: JSON.stringify(request),
    },
  );

  const json = await res.json<{ data: InitiateRightsRequestResponse }>();
  return json.data;
};

/**
 * Save rights configurations
 *
 * POST /api/rights-management/configurations
 */
export const postRightsConfiguration = async (
  request: IRightsOption,
  init?: RequestInit,
): Promise<IRightsOption> => {
  const res = await rightsManagementFetch(`configurations`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(request),
  });

  return res.json<IRightsOption>();
};

/**
 * Delete rights configurations
 *
 * DELETE /api/rights-management/configurations/:id
 */
export const deleteRightsConfiguration = async (
  req: { id: number },
  init?: RequestInit,
): Promise<RightsConfigurationsResponse> => {
  const res = await rightsManagementFetch(`configurations/${req.id}`, {
    ...init,
    method: 'DELETE',
  });

  return res.json<RightsConfigurationsResponse>();
};

/**
 * Get rights configurations
 *
 * GET /api/rights-management/configurations
 */
export const getRightsConfigurations = async (
  request: RightsConfigurationsRequest,
  init?: RequestInit,
): Promise<RightsConfigurationsResponse> => {
  const params = makeParams(request);
  const res = await rightsManagementFetch(`configurations?${params}`, {
    ...init,
  });

  return res.json();
};

/**
 * Get syndicated content status
 *
 * GET /api/v1/connect/bazaarvoice-network-media/${creatorClientId}/items/${cuid}/status
 */
export const getSyndicatedContentStatus = async (
  { creatorClientId, cuid }: SyndicatedContentStatusRequest,
  init?: RequestInit,
): Promise<SyndicatedContentStatusResponse> => {
  const res = await syndicationFetch(
    `bazaarvoice-network-media/${creatorClientId}/items/${cuid}/status`,
    { ...init },
  );

  return res.json();
};

/**
 * Get rights historical urls
 *
 * GET /api/v1/rights/terms-and-conditions/historical/url
 */
export const getRightsHistoricalUrls = async (
  init?: RequestInit,
): Promise<HistoricalUrlsResponse> => {
  const res = await gatewayRightsFetch(`terms-and-conditions/historical/url`, {
    ...init,
  });

  return res.json();
};

/**
 * Get rights capabilities for Multi handle
 *
 * GET /api/v2/universal-image-actions/items/:cuid/rights-graph-metadata
 */
export const getRightsCapabilitiesMultiHandle = async (
  req: { cuid: PartialCuid },
  init?: RequestInit,
): Promise<boolean> => {
  const res = await webGatewayFetch(
    `/api/v2/universal-image-actions/items/${req.cuid}/rights-graph-metadata`,
    {
      ...init,
    },
  );

  return (await res.json<IRightsCapabilitiesMultiHandleResponse>())
    .isRightsCapable;
};

/**
 * Detect language for rights message
 *
 * POST /api/v1/comprehend/detect-language
 */
export const detectLanguage = async (
  request: { message: string },
  init?: RequestInit,
): Promise<DetectLanguageResponse> => {
  const res = await webGatewayFetch(`/api/v1/comprehend/detect-language`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(request),
  });
  const response = await res.json<{ data: DetectLanguageResponse }>();
  return response.data;
};

/**
 * Set rights expiry for a cuid. Pass null to remove expiry.
 *
 * PUT /api/v1/rights/:cuid/expiration-time
 */
export const setRightsExpiryDate = async (
  request: SetRightsExpiryDateRequest,
  init?: RequestInit,
): Promise<void> => {
  await gatewayRightsFetch(`${request.cuid}/expiration-time`, {
    ...init,
    body: JSON.stringify({
      expirationTime: request.expirationTime,
    }),
    method: 'PUT',
  });
};
